/**
 * Validate-on-save for template editors (POST /api/template-variables/validate).
 *
 *   const check = useTemplateCheck('messages');
 *   const onSave = async () => {
 *     if (!(await check.run({ subject, body }))) return; // errors block the save
 *     …save…
 *   };
 *   <TemplateCheckNotice result={check.result} />
 *
 * Errors (unknown / inactive / not-available-here variables) block saving.
 * Warnings (variables only some sends can fill) are shown but do not block.
 * If the validate call itself fails, the save goes ahead: the send path still
 * refuses a message it cannot fill, so a flaky check must not lock editors.
 */

import React, { useCallback, useState } from 'react';
import { AlertTriangle, XCircle } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { templateVariablesApi, type CheckSurface, type UnresolvedVariable, type ValidateResponse } from './api';
import { reasonLabel } from './labels';

export interface TemplateCheckInput {
  subject?: string | null;
  body: string;
}

export function useTemplateCheck(appliesTo: CheckSurface) {
  const [result, setResult] = useState<ValidateResponse | null>(null);
  const [checking, setChecking] = useState(false);

  /** Validate; resolves true when saving may proceed. */
  const run = useCallback(
    async (input: TemplateCheckInput | TemplateCheckInput[]): Promise<boolean> => {
      const parts = Array.isArray(input) ? input : [input];
      setChecking(true);
      try {
        const results = await Promise.all(
          parts
            .filter((p) => (p.body || '').trim() || (p.subject || '').trim())
            .map((p) =>
              templateVariablesApi.validate({ subject: p.subject ?? null, body: p.body || '', applies_to: appliesTo }),
            ),
        );
        const merged = mergeResults(results);
        setResult(merged);
        return merged ? merged.ok : true;
      } catch {
        setResult(null);
        return true;
      } finally {
        setChecking(false);
      }
    },
    [appliesTo],
  );

  const clear = useCallback(() => setResult(null), []);

  return { run, result, checking, clear };
}

function dedupe(list: UnresolvedVariable[]): UnresolvedVariable[] {
  const seen = new Set<string>();
  return list.filter((u) => {
    const k = `${u.key}:${u.reason}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

function mergeResults(results: ValidateResponse[]): ValidateResponse | null {
  if (results.length === 0) return null;
  if (results.length === 1) return results[0];
  const errors = dedupe(results.flatMap((r) => r.errors ?? []));
  const warnings = dedupe(results.flatMap((r) => r.warnings ?? []));
  const inputs = new Map(results.flatMap((r) => r.required_inputs ?? []).map((i) => [i.key, i]));
  return {
    ok: errors.length === 0,
    variables: [...new Set(results.flatMap((r) => r.variables ?? []))],
    errors,
    warnings,
    required_inputs: [...inputs.values()],
  };
}

export function UnresolvedList({ items, tone }: { items: UnresolvedVariable[]; tone: 'error' | 'warning' }) {
  const { t } = useAppTranslation();
  return (
    <ul className="mt-1 space-y-1">
      {items.map((u) => (
        <li key={`${u.key}:${u.reason}`} className="text-xs">
          <code className={`font-mono font-semibold ${tone === 'error' ? 'text-danger' : 'text-attention-ink'}`}>
            {`{{${u.key}}}`}
          </code>
          <span className="text-ink-700"> — {u.message || reasonLabel(t, u.reason)}</span>
        </li>
      ))}
    </ul>
  );
}

export function TemplateCheckNotice({ result }: { result: ValidateResponse | null }) {
  const { t } = useAppTranslation();
  if (!result) return null;
  const { errors = [], warnings = [] } = result;
  if (errors.length === 0 && warnings.length === 0) return null;
  return (
    <div className="space-y-2" data-testid="template-check-notice">
      {errors.length > 0 && (
        <div role="alert" className="rounded-md border border-danger bg-danger-soft px-3 py-2">
          <p className="flex items-center gap-1.5 text-xs font-semibold text-danger">
            <XCircle className="h-3.5 w-3.5" aria-hidden />
            {t('templateVariables.check.errorsTitle', 'Fix these variables before saving')}
          </p>
          <UnresolvedList items={errors} tone="error" />
        </div>
      )}
      {warnings.length > 0 && (
        <div className="rounded-md border border-attention bg-attention-soft px-3 py-2">
          <p className="flex items-center gap-1.5 text-xs font-semibold text-attention-ink">
            <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
            {t('templateVariables.check.warningsTitle', 'Some sends cannot fill these variables')}
          </p>
          <UnresolvedList items={warnings} tone="warning" />
        </div>
      )}
    </div>
  );
}
